import React from "react";
import { Box, Button, Link as MuiLink, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import { Stack } from "components/layout/reusableLayouts";

export type StatusPageTone = "brand" | "success";

const MEDALLION: Record<StatusPageTone, { background: string; color: string }> =
  {
    brand: {
      background: "var(--color-bg-brand-subtle)",
      color: "var(--color-text-brand)",
    },
    success: {
      background: "var(--color-bg-success)",
      color: "var(--color-text-success)",
    },
  };

export interface StatusPageProps {
  /** Headline. Already translated. */
  title: string;
  /** One or two sentences on what is happening. Already translated. */
  message: string;
  /** The state, as a glyph. Drawn inside a tinted medallion. */
  icon: IconSvgElement;
  /** The medallion's tint. `success` is for a state that resolved in the reader's favour. */
  tone?: StatusPageTone;
  /** Detail under the message, such as the steps still to come. */
  children?: React.ReactNode;
  /**
   * The one thing to do next. Omit for a page with nothing to offer.
   *
   * A link or a handler, never both: `href` renders an anchor, and an anchor
   * that also runs a handler navigates and fires, which is two outcomes from
   * one button.
   */
  action?: { label: string } & (
    | { href: string; onClick?: never }
    | { onClick: () => void; href?: never }
  );
  /**
   * Shown under the action as "Need help? <email>". Omit where the action is
   * already the way to reach us, so the address is not offered twice.
   */
  supportEmail?: string;
}

/**
 * A full-page state the app puts someone in rather than a page they asked for:
 * maintenance, a deactivated company, an application under review, a
 * mobile-only account. One shell for all of them, because the
 * two that existed had drifted into copies of each other — same markup, one
 * with `elevation` and `variant="outlined"` fighting, one describing its
 * illustration as "Maintenance" on the deactivation screen.
 *
 * The illustrations went with them. Both were raster art with their own
 * headline baked in ("UNDER MAINTENANCE" above our "Maintenance in Progress"),
 * on a light ground that stayed light on a dark canvas. A glyph in a tinted
 * medallion says the same thing, in whichever scheme the reader is in.
 *
 * The shape is deliberate: say what happened, say what it means, offer exactly
 * one thing to do. Nobody reading this screen wants to choose.
 */
const StatusPage: React.FC<StatusPageProps> = ({
  title,
  message,
  icon,
  tone = "brand",
  children,
  action,
  supportEmail,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        paddingBlock: "var(--space-8)",
        paddingInline: "var(--space-5)",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 560,
          padding: { xs: "var(--space-7)", sm: "var(--space-8)" },
          borderRadius: "var(--radius-2xl)",
        }}
      >
        <Stack gap="var(--space-6)" sx={{ alignItems: "center" }}>
          <Box
            aria-hidden
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 72,
              height: 72,
              borderRadius: "var(--radius-full)",
              backgroundColor: MEDALLION[tone].background,
              color: MEDALLION[tone].color,
            }}
          >
            <HugeiconsIcon icon={icon} size={32} strokeWidth={1.5} />
          </Box>

          <Stack gap="var(--space-3)" sx={{ alignItems: "center" }}>
            <Typography variant="h5" sx={{ textAlign: "center" }}>
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "var(--color-text-secondary)",
                // Prose reads badly across the full card width.
                maxWidth: "44ch",
              }}
            >
              {message}
            </Typography>
          </Stack>

          {children}

          {action && (
            <Button
              variant="primary-filled"
              href={action.href}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}

          {supportEmail && (
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("Need help?")}{" "}
              <MuiLink href={`mailto:${supportEmail}`}>{supportEmail}</MuiLink>
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default StatusPage;
